import { useEffect, useState } from 'react';
import axios from 'axios';
import { usePageMeta } from '@/lib/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders a versioned legal policy fetched by slug.
 * Understands a small subset of markdown: `## Heading`, `- bullet`, blank-line paragraphs.
 */
export default function LegalPolicyPage({ slug, fallbackTitle }) {
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BACKEND_URL}/api/legal/policies/${slug}`)
      .then((r) => { if (alive) setPolicy(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Unable to load this policy.'); });
    return () => { alive = false; };
  }, [slug]);

  usePageMeta({
    title: `${policy?.title || fallbackTitle} | Toia Wallace`,
    description: `${policy?.title || fallbackTitle} for products and services purchased directly through IAmToiaWallace.com.`,
    path: `/${slug}`,
    robots: 'noindex, follow',
  });

  return (
    <div className="bg-ivory" data-testid={`legal-page-${slug}`}>
      <section className="pt-32 sm:pt-40 lg:pt-56 pb-12 lg:pb-24">
        <div className="mx-auto max-w-[1100px] px-6 sm:px-10 lg:px-16">
          <div className="eyebrow text-taupe mb-6 sm:mb-8">Legal · {policy?.title || fallbackTitle}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tightest text-matte">
            {(policy?.title || fallbackTitle).split(' ').slice(0, -1).join(' ')}
            <br />
            <em className="italic text-taupe">{(policy?.title || fallbackTitle).split(' ').slice(-1)}.</em>
          </h1>
          {policy && (
            <div className="mt-8 sm:mt-10 flex flex-wrap gap-x-8 gap-y-2 font-label text-[10px] tracking-[0.28em] uppercase text-taupe">
              <span>Effective: {policy.effective_date}</span>
              <span>Last Updated: {policy.last_updated}</span>
              <span>Version: {policy.version}</span>
            </div>
          )}
        </div>
      </section>

      <section className="pb-24 lg:pb-40">
        <div className="mx-auto max-w-[820px] px-6 sm:px-10">
          <div className="hairline-gold h-px w-16 mb-10 sm:mb-12" aria-hidden />
          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}
          {policy && (
            <PolicyMarkdown content={policy.content_markdown} />
          )}
          {!policy && !error && (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-taupe/20 rounded w-3/4"></div>
              <div className="h-4 bg-taupe/20 rounded w-full"></div>
              <div className="h-4 bg-taupe/20 rounded w-5/6"></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function PolicyMarkdown({ content }) {
  const blocks = parseMarkdown(content);
  return (
    <article className="legal-body space-y-5 text-matte/80 leading-[1.85] text-[15px] sm:text-[16px]">
      {blocks.map((b, i) => {
        if (b.type === 'h2') {
          return (
            <h2 key={i} className="mt-10 sm:mt-12 mb-2 font-serif-display text-2xl sm:text-3xl text-matte tracking-tight leading-snug">
              {b.text}
            </h2>
          );
        }
        if (b.type === 'ul') {
          return (
            <ul key={i} className="list-disc pl-6 space-y-1.5 marker:text-gold">
              {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
            </ul>
          );
        }
        if (b.type === 'em') {
          return (
            <p key={i} className="italic text-taupe/90 border-l-2 border-gold/40 pl-4">
              {b.text}
            </p>
          );
        }
        return <p key={i}>{renderInline(b.text)}</p>;
      })}
    </article>
  );
}

function parseMarkdown(md) {
  const lines = (md || '').split(/\r?\n/);
  const blocks = [];
  let paraBuf = [];
  let listBuf = [];

  const flushPara = () => {
    if (paraBuf.length) {
      const text = paraBuf.join(' ').trim();
      if (text) {
        // Detect *italic* full-line block (single asterisk-wrapped)
        if (text.startsWith('*') && text.endsWith('*') && !text.startsWith('**')) {
          blocks.push({ type: 'em', text: text.slice(1, -1).trim() });
        } else {
          blocks.push({ type: 'p', text });
        }
      }
      paraBuf = [];
    }
  };
  const flushList = () => {
    if (listBuf.length) {
      blocks.push({ type: 'ul', items: listBuf });
      listBuf = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith('## ')) {
      flushPara();
      flushList();
      blocks.push({ type: 'h2', text: line.slice(3).trim() });
      continue;
    }
    if (/^\s*-\s+/.test(line)) {
      flushPara();
      listBuf.push(line.replace(/^\s*-\s+/, '').trim());
      continue;
    }
    // paragraph continuation
    flushList();
    paraBuf.push(line);
  }
  flushPara();
  flushList();
  return blocks;
}

// Minimal inline formatter: **bold**, *italic*
function renderInline(text) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) parts.push(<strong key={parts.length}>{tok.slice(2, -2)}</strong>);
    else parts.push(<em key={parts.length} className="italic">{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
